import { useState, useEffect, useRef } from 'react';
import PrefetchLink from '../common/PrefetchLink';
import { useTemplates } from '../../context/TemplatesContext';
import { useAuth } from '../../context/AuthContext';
import { lockScroll, unlockScroll } from '../../utils/scrollLock';
import './Header.css';
import defaultLogo from '../../assets/logo.png';
import { FaHome, FaFileAlt, FaFileInvoiceDollar, FaUsers, FaCaretDown, FaUserCircle, FaSignOutAlt, FaAddressBook, FaBars, FaTimes, FaBox, FaBuilding } from 'react-icons/fa';

const Header = () => {
  const { templates } = useTemplates();
  const { currentUser, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openNavDropdown, setOpenNavDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navDropdownRefs = useRef({});

  // Use uploaded logo if available, otherwise use default
  const logoSrc = templates?.companyLogoBase64 || defaultLogo;

  // Use company name from templates (unified source)
  const companyName = templates?.companyName || currentUser?.companyName || '公司';
  
  // Check different user roles
  const isSuperAdmin = currentUser && currentUser.role === 'superadmin';
  const isAdmin = currentUser && currentUser.role === 'admin';

  // Check permissions for different menu items
  const canManageUsers = isSuperAdmin || isAdmin; // Only superadmin and full admin can manage users
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Close mobile menu when clicking outside or on ESC key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        const hamburger = document.querySelector('.hamburger-menu');
        if (hamburger && !hamburger.contains(event.target)) {
          setIsMobileMenuOpen(false);
          setOpenNavDropdown(null);
        }
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
        setOpenNavDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    // Prevent body scroll when mobile menu is open（使用全域引用計數）
    if (isMobileMenuOpen) {
      lockScroll();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      if (isMobileMenuOpen) {
        unlockScroll();
      }
    };
  }, [isMobileMenuOpen]);

  // Close nav dropdowns when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openNavDropdown && window.innerWidth > 1024) {
        const isClickInsideDropdown = Object.values(navDropdownRefs.current).some(ref =>
          ref && ref.contains(event.target)
        );
        if (!isClickInsideDropdown) {
          setOpenNavDropdown(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openNavDropdown]);
  
  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLogout = () => {
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    logout();
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setOpenNavDropdown(null);
  };

  const toggleNavDropdown = (menuName) => {
    setOpenNavDropdown(openNavDropdown === menuName ? null : menuName);
  };
  
  return (
    <header className="header">
      <div className="container">
        <div className="header-content">
          <div className="logo">
            <PrefetchLink to="/" onClick={closeMobileMenu}>
              <img src={logoSrc} alt="公司 LOGO" />
            </PrefetchLink>
          </div>
          
          {/* 漢堡選單按鈕 (僅在手機版顯示) */}
          <button className="hamburger-menu" onClick={toggleMobileMenu} aria-label="選單">
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
          
          {/* 遮罩層 */}
          {isMobileMenuOpen && <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>}
          
          <nav className={`nav ${isMobileMenuOpen ? 'mobile-open' : ''}`} ref={mobileMenuRef}>
            <ul>
              <li>
                <PrefetchLink to="/" onClick={closeMobileMenu}><FaHome /> 儀表板</PrefetchLink>
              </li>

              {/* 報價管理下拉選單 */}
              <li className="nav-dropdown" ref={el => navDropdownRefs.current['quotation'] = el}>
                <div
                  className="nav-dropdown-toggle"
                  onClick={() => toggleNavDropdown('quotation')}
                >
                  <span><FaFileAlt /> 報價管理</span>
                  <FaCaretDown className={openNavDropdown === 'quotation' ? 'rotate' : ''} />
                </div>
                {openNavDropdown === 'quotation' && (
                  <ul className="nav-dropdown-menu">
                    <li>
                      <PrefetchLink to="/create" onClick={closeMobileMenu}>新增報價單</PrefetchLink>
                    </li>
                    <li>
                      <PrefetchLink to="/quotations" onClick={closeMobileMenu}>報價單列表</PrefetchLink>
                    </li>
                  </ul>
                )}
              </li>

              {/* 器材管理下拉選單 */}
              <li className="nav-dropdown" ref={el => navDropdownRefs.current['asset'] = el}>
                <div
                  className="nav-dropdown-toggle"
                  onClick={() => toggleNavDropdown('asset')}
                >
                  <span><FaBox /> 器材管理</span>
                  <FaCaretDown className={openNavDropdown === 'asset' ? 'rotate' : ''} />
                </div>
                {openNavDropdown === 'asset' && (
                  <ul className="nav-dropdown-menu">
                    <li>
                      <PrefetchLink to="/assets" onClick={closeMobileMenu}>資產表</PrefetchLink>
                    </li>
                    <li>
                      <PrefetchLink to="/schedule" onClick={closeMobileMenu}>檔期大表</PrefetchLink>
                    </li>
                  </ul>
                )}
              </li>

              {/* 營收管理下拉選單 */}
              <li className="nav-dropdown" ref={el => navDropdownRefs.current['revenue'] = el}>
                <div
                  className="nav-dropdown-toggle"
                  onClick={() => toggleNavDropdown('revenue')}
                >
                  <span><FaFileInvoiceDollar /> 營收管理</span>
                  <FaCaretDown className={openNavDropdown === 'revenue' ? 'rotate' : ''} />
                </div>
                {openNavDropdown === 'revenue' && (
                  <ul className="nav-dropdown-menu">
                    <li>
                      <PrefetchLink to="/invoices" onClick={closeMobileMenu}>請款單</PrefetchLink>
                    </li>
                    <li>
                      <PrefetchLink to="/dashboard" onClick={closeMobileMenu}>營收分析</PrefetchLink>
                    </li>
                  </ul>
                )}
              </li>

              {/* 客戶管理下拉選單 */}
              <li className="nav-dropdown" ref={el => navDropdownRefs.current['customer'] = el}>
                <div
                  className="nav-dropdown-toggle"
                  onClick={() => toggleNavDropdown('customer')}
                >
                  <span><FaAddressBook /> 客戶管理</span>
                  <FaCaretDown className={openNavDropdown === 'customer' ? 'rotate' : ''} />
                </div>
                {openNavDropdown === 'customer' && (
                  <ul className="nav-dropdown-menu">
                    <li>
                      <PrefetchLink to="/customers/new" onClick={closeMobileMenu}>新增客戶</PrefetchLink>
                    </li>
                    <li>
                      <PrefetchLink to="/customers" onClick={closeMobileMenu}>客戶列表</PrefetchLink>
                    </li>
                  </ul>
                )}
              </li>
              {currentUser && (
                <li className="user-menu" ref={dropdownRef}>
                  <div className="user-menu-toggle" onClick={toggleDropdown}>
                    <span>{companyName} - {currentUser.name}</span>
                    <FaCaretDown className={isDropdownOpen ? 'rotate' : ''} />
                  </div>
                  {isDropdownOpen && (
                    <ul className="dropdown-menu">
                      <li>
                        <PrefetchLink to="/profile" onClick={() => { setIsDropdownOpen(false); closeMobileMenu(); }}>
                          <FaUserCircle /> 個人資料
                        </PrefetchLink>
                      </li>

                      {/* 只有超級管理員和一般管理員可以管理用戶 */}
                      {canManageUsers && (
                        <li>
                          <PrefetchLink
                            to={isSuperAdmin ? "/users" : `/companies/${currentUser.company?._id || currentUser.company}/users`}
                            onClick={() => { setIsDropdownOpen(false); closeMobileMenu(); }}
                          >
                            <FaUsers /> 用戶管理
                          </PrefetchLink>
                        </li>
                      )}
                      {/* 只有公司管理員可以查看公司基本資料設定 */}
                      {isAdmin && (
                        <li>
                          <PrefetchLink
                            to="/company-settings"
                            onClick={() => { setIsDropdownOpen(false); closeMobileMenu(); }}
                          >
                            <FaBuilding /> 公司基本資料
                          </PrefetchLink>
                        </li>
                      )}
                      <li>
                        <button onClick={handleLogout}>
                          <FaSignOutAlt /> 登出
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
